import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Flame, Target } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { formatMillisecondsToMMSS } from "@/lib/time";
import { roundToDecimalsFixed } from "@/lib/metrics";
import { useTranslation } from "react-i18next";

export interface StreakCardProps {
  currentStreak: number;
  todayTimeSpent: number; // in minutes
  requiredTimeForStreak: number; // in minutes
  lastActiveDate: string;
}

export function StreakCard({
  currentStreak,
  todayTimeSpent,
  requiredTimeForStreak,
  lastActiveDate,
}: StreakCardProps) {
  const { t } = useTranslation(["pages", "error"]);

  // Format time in hours and minutes
  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Calculate progress percentage
  const getProgressPercentage = (current: number, required: number) => {
    return Math.min((current / required) * 100, 100);
  };

  // Check if today's goal is achieved
  const isTodayGoalAchieved = (current: number, required: number) => {
    return current >= required;
  };

  if (!currentStreak && !todayTimeSpent && !requiredTimeForStreak) {
    return (
      <Card className="w-full">
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-muted-foreground">
            {t("error:errorLoadingData.description")}
          </div>
        </CardContent>
      </Card>
    );
  }

  const progressPercentage = getProgressPercentage(
    todayTimeSpent,
    requiredTimeForStreak
  );
  const goalAchieved = isTodayGoalAchieved(
    todayTimeSpent,
    requiredTimeForStreak
  );
  const remainingTime = roundToDecimalsFixed(
    Math.max(0, requiredTimeForStreak - todayTimeSpent),
    2
  );

  return (
    <Card className="w-full col-span-2">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Flame className="h-5 w-5 text-orange-500" />
          {t("pages:dashboard.streak.title")}
          <Badge
            variant={currentStreak > 0 ? "default" : "secondary"}
            className="ml-auto"
          >
            {t("pages:dashboard.streak.daysBadge", { currentStreak })}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 p-10">
        {/* Current Streak Display */}
        <div className="text-center">
          <div className="text-5xl font-bold text-orange-500 mb-1">
            {currentStreak}
          </div>
          <div className="text-sm text-muted-foreground">
            {currentStreak === 1
              ? t("pages:dashboard.streak.dayStreak_one")
              : t("pages:dashboard.streak.dayStreak_other")}
          </div>
          {currentStreak == 0 && (
            <div className="text-xs text-muted-foreground">
              {t("pages:dashboard.streak.lastActiveDate", { lastActiveDate })}
            </div>
          )}
        </div>

        {/* Today's Progress */}
        <div className="space-y-2 pt-5 pb-10">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-blue-500" />
              <span>{t("pages:dashboard.streak.todayProgress")}</span>
            </div>
            <span className="font-medium">
              {formatMillisecondsToMMSS(todayTimeSpent * 60 * 1000)} /{" "}
              {formatMillisecondsToMMSS(requiredTimeForStreak * 60 * 1000)}
            </span>
          </div>

          <Progress value={progressPercentage} className="h-2" />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {t("pages:dashboard.streak.percentageCompleted", {
                percentageCompleted: Math.round(progressPercentage),
              })}
            </span>
            {goalAchieved ? (
              <Badge variant="default" className="text-xs bg-green-500">
                {t("pages:dashboard.streak.goalAchieved")}
              </Badge>
            ) : (
              <span>
                {t("pages:dashboard.streak.remainingTime", { remainingTime })}
              </span>
            )}
          </div>
        </div>

        {/* Goal Information */}
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm">
            <Target className="h-4 w-4 text-green-500" />
            <span className="font-medium">
              {t("pages:dashboard.streak.dailyGoal")}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {t("pages:dashboard.streak.spendTimeMessage", {
              requiredTimeForStreak: formatTime(requiredTimeForStreak),
            })}
          </div>
        </div>

        {/* Motivational Message */}
        <div className="text-center text-sm">
          {goalAchieved ? (
            <span className="text-green-600 font-medium">
              {t("pages:dashboard.streak.congratulationsMessage")}
            </span>
          ) : currentStreak > 0 ? (
            <span className="text-orange-600">
              {t("pages:dashboard.streak.keepGoingMessage", { currentStreak })}
            </span>
          ) : (
            <span className="text-muted-foreground">
              {t("pages:dashboard.streak.startStreak", {
                requiredTimeForStreak: formatTime(requiredTimeForStreak),
              })}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
